import { AuditEvent, ListAuditTrailParams, listAuditTrails } from '@brands/services/audit_trails/listAuditTrails';
import uniqBy from 'lodash/uniqBy';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Meta } from '../services/communication/types/Meta';
import { displayErrorDetails } from '../Utils/displayError';

interface IUseConversations {
  auditTrailList: AuditEvent[];
  reloadAll: () => Promise<void>;
  loadNextPage: () => Promise<void>;
  loadPreviousPage: () => Promise<void>;
  dataLoading: boolean;
  loading: boolean;
  hasNextPage: boolean;
  totalRecords: number;
}

type CursorSnapshot = {
  start: string;
  end: string;
};

type UseAuditTrailOptions = {
  paginationMode?: 'append' | 'page';
};

export default function useAuditTrail(
  params: Pick<
    ListAuditTrailParams,
    | 'actor_name'
    | 'sort_by'
    | 'sort_direction'
    | 'date_from'
    | 'date_to'
    | 'user_name'
    | 'user_role'
    | 'search'
    | 'limit'
    | 'user_id'
    | 'action_message'
    | 'users_only'
    | 'include_case_creation'
    | 'case_id'
  >,
  userRole?: string,
  options?: UseAuditTrailOptions,
): IUseConversations {
  const [auditTrailList, setAuditTrailList] = useState<AuditEvent[]>([]);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [firstPageMeta, setFirstPageMeta] = useState<Meta | null>(null);
  const [lastPageMeta, setLastPageMeta] = useState<Meta | null>(null);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [cursorStack, setCursorStack] = useState<CursorSnapshot[]>([]);
  const paginationMode = options?.paginationMode ?? 'append';

  const queryString = useMemo(
    () =>
      Object.keys(params)
        .map((key) => {
          const value = params[key as keyof typeof params];
          if (Array.isArray(value)) {
            return value.map((v) => `${key}=${encodeURIComponent(v)}`).join('&');
          }
          return `${key}=${encodeURIComponent(value as string | number | boolean)}`;
        })
        .join('&'),
    [params],
  );

  const loadAll = useCallback(async (): Promise<void> => {
    setDataLoading(true);
    try {
      const auditTrails = await listAuditTrails(queryString, userRole);
      setAuditTrailList(auditTrails.nodes);
      setFirstPageMeta(auditTrails.meta);
      setLastPageMeta(auditTrails.meta);
      setTotalRecords(auditTrails.total ?? 0);
      setCursorStack([]);
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
    setDataLoading(false);
  }, [queryString, userRole]);

  const reloadAll = useCallback(async (): Promise<void> => {
    setDataLoading(true);
    try {
      const auditTrails = await listAuditTrails(queryString, userRole);
      setAuditTrailList(auditTrails.nodes);
      setFirstPageMeta(auditTrails.meta);
      setLastPageMeta(auditTrails.meta);
      setTotalRecords(auditTrails.total ?? 0);
      setCursorStack([]);
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
    setDataLoading(false);
  }, [queryString, userRole]);

  const loadNextPage = async (): Promise<void> => {
    if (!lastPageMeta?.has_next_page) {
      return;
    }
    const previousSnapshot = {
      start: lastPageMeta.start_cursor,
      end: lastPageMeta.end_cursor,
    };
    setLoading(true);
    try {
      const { meta, nodes } = await listAuditTrails(
        `${queryString}&${`cursor=${lastPageMeta.end_cursor}`}&cursor_direction=${`next`}`,
        userRole,
      );
      setLastPageMeta(meta);
      if (paginationMode === 'page') {
        setFirstPageMeta(meta);
      } else if (!firstPageMeta) {
        setFirstPageMeta(meta);
      }
      if (paginationMode === 'page') {
        setCursorStack((prev) => [...prev, previousSnapshot]);
        setAuditTrailList(nodes);
      } else {
        setAuditTrailList((prevCases) => uniqBy([...prevCases, ...nodes], 'id'));
      }
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
    setLoading(false);
  };

  const loadPreviousPage = async (): Promise<void> => {
    if (paginationMode === 'page' && cursorStack.length === 0) {
      return;
    }
    if (paginationMode !== 'page' && !firstPageMeta?.has_previous_page) {
      return;
    }
    if (paginationMode === 'page' && cursorStack.length === 1) {
      setCursorStack([]);
      await loadAll();
      return;
    }
    if (!firstPageMeta?.start_cursor) {
      return;
    }
    try {
      const { meta, nodes } = await listAuditTrails(
        `${queryString}&cursor=${firstPageMeta.start_cursor}&cursor_direction=${`previous`}`,
        userRole,
      );
      setFirstPageMeta(meta);
      if (paginationMode === 'page') {
        setLastPageMeta(meta);
      } else if (!lastPageMeta) {
        setLastPageMeta(meta);
      }
      if (paginationMode === 'page') {
        setCursorStack((prev) => prev.slice(0, -1));
        setAuditTrailList(nodes);
      } else {
        setAuditTrailList((prevCases) => uniqBy([...nodes, ...prevCases], 'id'));
      }
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
  };

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  return {
    auditTrailList,
    reloadAll,
    loadNextPage,
    loadPreviousPage,
    dataLoading,
    loading,
    hasNextPage: !!lastPageMeta?.has_next_page,
    totalRecords,
  };
}
